import httpStatus from 'http-status'
import { nanoid } from 'nanoid'
import Sparkie from '@hotmart-org-ca/sparkie'
import Request from '@hotmart-org-ca/request'

import Settings from 'common/helpers/settings/.'
import RequestHelper from 'common/helpers/request'
import AuthService from 'modules/auth/service'
import FallbackHelper from 'common/helpers/fallback'
import { MessageReceivedPayload } from 'modules/chat/types'
import {
  InitializeSparkiePayload,
  GetMessagesPayload,
  SendMessagePayload,
  Actions,
  AnonymousPayload,
  ActionsName,
  C3POProfile
} from '../types'
import './request'

const firebaseConfig = Settings.sparkie.firebaseConfig
const sparkie = new Sparkie({
  firebaseConfig,
  options: {
    conversationAPI: Settings.api.c3po_conversations,
    authURL: `${Settings.api.c3po_auth}/v1`,
    numMaxMessages: 20
  }
})
const ACTION_EVENT_MAP: {[action in ActionsName]: string} = {
  messageReceived: 'messages.create',
  messageDeleted: 'messages.delete',
  visibilityChanged: 'visibility.change',
  onlineCounterUpdate: 'onlineCounter.update',
  participantUpdate: 'participants.update'
}

export default class ChatService {
  static async initializeSparkie(parameters: InitializeSparkiePayload) {
    const { token, webinarConversationId, skipPresenceSetup = false } = parameters

    if (sparkie.db) {
      return sparkie
    }

    const response = await sparkie.init(token, { skipPresenceSetup, webinarConversationId })

    return response
  }

  static getSparkie() {
    return sparkie
  }

  static subscribeEvents(actions: Actions) {
    const {
      messageReceived,
      messageDeleted,
      visibilityChanged,
      onlineCounterUpdate,
      participantUpdate
    } = actions

    // Message Events
    if (messageReceived) {
      sparkie.on(
        ACTION_EVENT_MAP.messageReceived,
        messageReceived
      )
    }

    if (messageDeleted) {
      sparkie.on(
        ACTION_EVENT_MAP.messageDeleted,
        messageDeleted
      )
    }

    // Webinar Events
    if (visibilityChanged) {
      sparkie.on(
        ACTION_EVENT_MAP.visibilityChanged,
        (data: boolean) => visibilityChanged(data)
      )
    }

    if (onlineCounterUpdate) {
      sparkie.on(
        ACTION_EVENT_MAP.onlineCounterUpdate,
        (data: number) => onlineCounterUpdate(data)
      )
    }

    participantUpdate && sparkie.on(ACTION_EVENT_MAP.participantUpdate, participantUpdate)
  }

  static removeEventSubscription(actions: Actions) {
    Object.values(ActionsName).forEach(actionName => {
      const listener = actions[actionName]

      listener && sparkie.off(ACTION_EVENT_MAP[actionName], listener)
    })
  }

  static destroySparkie() {
    sparkie.destroy()
  }

  // Message
  static async getMessages(parameters: GetMessagesPayload) {
    try {
      const { conversationId, before = void 0 } = parameters

      const response = await sparkie?.messageService?.getAll(
        conversationId,
        { before }
      )

      return response || []
    } catch (error) {
      throw RequestHelper.errorHandling(error)
    }
  }

  static async sendTextMessage(parameters: SendMessagePayload) {
    try {
      const { conversationId, message, reply } = parameters

      const messageSent = await sparkie.messageService.post(
        conversationId,
        {
          content: {
            type: 'text/plain',
            text: message
          },
          metadata: {
            externalId: nanoid()
          },
          ...(reply?.messageId && {
            parentId: reply.messageId
          })
        }
      )

      return messageSent as MessageReceivedPayload
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const status = error?.response?.status

      if (status !== httpStatus.FORBIDDEN) {
        throw RequestHelper.errorHandling(error)
      }
    }

    return null
  }

  // Conversation
  static async getConversation(conversationId: string) {
    try {
      const response = await sparkie.conversationService.get(conversationId)

      return response
    } catch (error) {
      throw RequestHelper.errorHandling(error)
    }
  }

  static async joinConversation(conversationId: string) {
    try {
      const response = await sparkie.conversationService.join(conversationId)

      return response
    } catch (error) {
      throw RequestHelper.errorHandling(error)
    }
  }

  static async getProfile() {
    try {
      const options = AuthService.isAnonymous()
        ? {
          headers: {
            authorization: `Bearer ${AuthService.getAnonymousToken()}`
          }
        }
        : void 0

      const response = await Request('get.profile', void 0, options)
      const { id, name, picture, user_id: userId } = response

      return {
        id,
        name,
        picture,
        userId
      } as C3POProfile
    } catch (error) {
      throw RequestHelper.errorHandling(error)
    }
  }

  static async getAnonymousToken(payload: AnonymousPayload) {
    try {
      const currentToken = AuthService.getAnonymousToken()

      if (currentToken) {
        return currentToken
      }

      const response = await Request('post.auth.anonymous', payload)
      const { token } = response

      if (token) {
        AuthService.setAnonymousToken(token)
      }

      return token
    } catch (error) {
      throw RequestHelper.errorHandling(error)
    }
  }

  static async getContactId() {
    let contactId = ''

    const token = (AuthService.getToken() || AuthService.getAnonymousToken()) ?? ''

    if (token) {
      const { id } = await ChatService.getProfile()

      contactId = id
    }

    return contactId
  }

  static configRealtimeModule = async ({
    conversationId,
    name,
    email
  }: { conversationId: string, name: string, email: string }) => {
    if (!conversationId || !name || !email) {
      return
    }

    const token = AuthService.getToken()
    let props = {
      token,
      webinarConversationId: conversationId
    }

    if (AuthService.isAnonymous()) {
      const anonymousToken = await ChatService.getAnonymousToken({
        name,
        email
      })

      props = {
        ...props,
        token: anonymousToken
      }
    }

    await ChatService.initializeSparkie(props)
  }

  static getChatAdmins = async (conversationId: string) => {
    try {
      const { adminIds = [], ownerId = '' } = await sparkie.listener.getWebinar(conversationId) || {}

      return [
        ...adminIds,
        ...(ownerId && [ownerId])
      ]
    } catch (error) {
      console.error(error)

      return []
    }
  }

  static toggleChatVisibility = (isActive: boolean) => {
    return FallbackHelper.retry(
      async () => {
        await sparkie.listener.setWebinarVisibility(isActive)

        return true
      },
      { timeoutMs: 10000 }
    )
  }
}
